/**
 *  Copyright AlpineReplay Inc, 2022. All rights reserved.
 *  Authors: Drew Burnett
 */

// Imports

import {
    useFindProfileByUserIdLazyQuery,
    useFindProfileQuery,
    useGetUserRelationsQuery,
} from "../generated/graphql"
import { useDispatch } from "react-redux"
import { useEffect, useState } from "react"

// Fetch User and Realtions Profiles

export const useFetchUserAndRelationsProfiles = async (user, token) => {
    const loggedInUserId = user ? user.user_id : null

    const [family, setFamily] = useState(null)
    const [profile, setProfile] = useState(null)
    const [familyProfiles, setFamilyProfiles] = useState(null)

    // We're going to need to dispatch
    const dispatch = useDispatch()

    // Is the token ready?
    const tokenReady = token !== null

    useEffect(() => {
        if (user && profile && user.user_id !== profile.user_id) {
            setFamilyProfiles(null)
            setFamily(null)
            setProfile(null)
        }
    }, [user])

    // GET the logged-in user's PROFILE
    const { data: profileData } = useFindProfileQuery({
        variables: {
            user_id: parseInt(loggedInUserId),
            token: token,
        },
        skip: !tokenReady || !loggedInUserId,
    })
    if (!profile && profileData) {
        // dispatch the logged-in user's profile to redux
        setProfile(profileData)
        dispatch({
            type: "COMMON_SET",
            key: "userProfile",
            value: { value: profileData },
        })
    }

    // GET the logged-in user's RELATIONS (these are user objects)
    const { data: relationsData } = useGetUserRelationsQuery({
        variables: {
            to_user_id: loggedInUserId,
            token: token,
            include_self: false,
        },
        skip: !tokenReady || !loggedInUserId || family,
    })
    if (!family && relationsData && relationsData.userRelations.length > 0) {
        // dispatch the logged-in user's RELATIONS
        setFamily(relationsData.userRelations)
        dispatch({
            type: "COMMON_SET",
            key: "family",
            value: { value: relationsData.userRelations },
        })
    }

    // GET the PROFILES for each of the logged-in user's relations
    let retFamilyProfiles = []
    const [getProfile] = useFindProfileByUserIdLazyQuery({
        variables: {
            user_id: parseInt(token?.user_id),
            token: token,
        },
        skip: !token,
        fetchPolicy: "network-only",
    })
    if (!familyProfiles && relationsData && relationsData.userRelations) {
        for await (const relation of relationsData?.userRelations) {
            // Fetch the user's profile
            const res = await getProfile({
                variables: {
                    user_id: parseInt(relation.user_id),
                    token: token,
                },
            })
            if (res.data) {
                retFamilyProfiles.push(res.data.profile)
            }
        }
        if (retFamilyProfiles && retFamilyProfiles.length) {
            setFamilyProfiles(retFamilyProfiles)
            dispatch({
                type: "COMMON_SET",
                key: "familyProfiles",
                value: { value: retFamilyProfiles },
            })
        }
    }
}
